import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { verifyToken } from '../Apis/LoginApis';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const refreshUser = useCallback(async () => {
        const savedUser = sessionStorage.getItem('iti_company_user');
        if (!savedUser) {
            setLoading(false);
            return;
        }

        try {
            const parsedUser = JSON.parse(savedUser);
            // We need the token to be set in headers, which apiClient interceptor handles
            // but we need to make sure the token is actually in the savedUser object
            if (parsedUser.iti_active_token) {
                const response = await verifyToken();
                if (response.success && response.data.user) {
                    const updatedUser = {
                        ...response.data,
                        iti_active_token: parsedUser.iti_active_token
                    };
                    setUser(updatedUser);
                    sessionStorage.setItem('iti_company_user', JSON.stringify(updatedUser));
                    
                    // Check if user needs to change password
                    if (!updatedUser.user?.isPasswordChanged && 
                        location.pathname !== '/force-password-change' && 
                        location.pathname !== '/login') {
                        navigate('/force-password-change', { replace: true });
                    }
                } else {
                    setUser(parsedUser);
                    
                    // Check if user needs to change password (from cached data)
                    if (!parsedUser.user?.isPasswordChanged && 
                        location.pathname !== '/force-password-change' && 
                        location.pathname !== '/login') {
                        navigate('/force-password-change', { replace: true });
                    }
                }
            } else {
                setUser(parsedUser);
                
                // Check if user needs to change password (from cached data)
                if (!parsedUser.user?.isPasswordChanged && 
                    location.pathname !== '/force-password-change' && 
                    location.pathname !== '/login') {
                    navigate('/force-password-change', { replace: true });
                }
            }
        } catch (e) {
            console.error("Failed to refresh user profile", e);
            // If it's a 401, we might want to logout, but for now just use cached data
            const savedUser = sessionStorage.getItem('iti_company_user');
            if (savedUser) {
                const parsedUser = JSON.parse(savedUser);
                setUser(parsedUser);
                
                // Check if user needs to change password (from cached data)
                if (!parsedUser.user?.isPasswordChanged && 
                    location.pathname !== '/force-password-change' && 
                    location.pathname !== '/login') {
                    navigate('/force-password-change', { replace: true });
                }
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, location.pathname]);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const login = (userData) => {
        setUser(userData);
        sessionStorage.setItem('iti_company_user', JSON.stringify(userData));
        
        // Check if user needs to change password immediately after login
        if (!userData.user?.isPasswordChanged && location.pathname !== '/force-password-change') {
            navigate('/force-password-change', { replace: true });
        }
    };

    const logout = () => {
        setUser(null);
        sessionStorage.removeItem('iti_company_user');
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, loading, refreshUser }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
